import type { Project } from "@/data";

const ADMIN_PROJECTS_KEY = "portfolio.admin.projects.v1";
const HIDDEN_DEFAULT_PROJECT_IDS_KEY = "portfolio.hidden.default.projects.v1";
const MAX_ADMIN_PROJECTS = 24;

export const PROJECTS_UPDATED_EVENT = "portfolio-admin-projects-updated";

const isBrowser = () => typeof window !== "undefined";

export const normalizeProjectLink = (link: string) => {
  const trimmedLink = link.trim();

  if (!trimmedLink) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmedLink)) {
    return trimmedLink;
  }

  return `https://${trimmedLink.replace(/^\/+/, "")}`;
};

const normalizeProjectId = (projectId: number | string) => String(projectId).trim();

const isProjectRecord = (value: unknown): value is Project => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const project = value as Partial<Project>;

  return (
    (typeof project.id === "string" || typeof project.id === "number") &&
    typeof project.title === "string" &&
    typeof project.des === "string" &&
    typeof project.img === "string" &&
    Array.isArray(project.iconLists) &&
    typeof project.link === "string"
  );
};

const sanitizeProject = (project: Project): Project => ({
  id: project.id,
  title: project.title.trim(),
  des: project.des.trim(),
  img: project.img.trim() || "/p1.svg",
  iconLists: project.iconLists.filter((icon) => typeof icon === "string" && icon.trim().length > 0),
  link: normalizeProjectLink(project.link),
  source: "admin",
  createdAt: project.createdAt ?? new Date().toISOString(),
});

const emitProjectsUpdated = () => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new Event(PROJECTS_UPDATED_EVENT));
};

export const getHiddenDefaultProjectIds = (): string[] => {
  if (!isBrowser()) {
    return [];
  }

  const rawIds = window.localStorage.getItem(HIDDEN_DEFAULT_PROJECT_IDS_KEY);
  if (!rawIds) {
    return [];
  }

  try {
    const parsedIds: unknown = JSON.parse(rawIds);
    if (!Array.isArray(parsedIds)) {
      return [];
    }

    const normalizedIds = parsedIds
      .filter((id): id is string | number => typeof id === "string" || typeof id === "number")
      .map((id) => normalizeProjectId(id))
      .filter((id) => id.length > 0);

    return Array.from(new Set(normalizedIds));
  } catch {
    return [];
  }
};

const saveHiddenDefaultProjectIds = (projectIds: string[]) => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(
    HIDDEN_DEFAULT_PROJECT_IDS_KEY,
    JSON.stringify(Array.from(new Set(projectIds)))
  );
  emitProjectsUpdated();
};

export const getAdminProjects = (): Project[] => {
  if (!isBrowser()) {
    return [];
  }

  const rawProjects = window.localStorage.getItem(ADMIN_PROJECTS_KEY);

  if (!rawProjects) {
    return [];
  }

  try {
    const parsedProjects: unknown = JSON.parse(rawProjects);

    if (!Array.isArray(parsedProjects)) {
      return [];
    }

    return parsedProjects
      .filter(isProjectRecord)
      .map(sanitizeProject)
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
  } catch {
    return [];
  }
};

export const saveAdminProjects = (projects: Project[]) => {
  if (!isBrowser()) {
    return;
  }

  const nextProjects = projects
    .map(sanitizeProject)
    .slice(0, MAX_ADMIN_PROJECTS);

  window.localStorage.setItem(ADMIN_PROJECTS_KEY, JSON.stringify(nextProjects));
  emitProjectsUpdated();
};

export const addAdminProject = (
  project: Omit<Project, "id" | "source" | "createdAt"> & { id?: number | string }
) => {
  const nextProject: Project = sanitizeProject({
    id: project.id ?? `admin-${Date.now()}`,
    title: project.title,
    des: project.des,
    img: project.img,
    iconLists: project.iconLists,
    link: project.link,
    source: "admin",
    createdAt: new Date().toISOString(),
  });

  const currentProjects = getAdminProjects().filter(
    (existingProject) => String(existingProject.id) !== String(nextProject.id)
  );

  saveAdminProjects([nextProject, ...currentProjects]);
};

export const removeAdminProject = (projectId: number | string) => {
  const filteredProjects = getAdminProjects().filter(
    (project) => String(project.id) !== String(projectId)
  );

  saveAdminProjects(filteredProjects);
};

export const hideDefaultProject = (projectId: number | string) => {
  const normalizedId = normalizeProjectId(projectId);
  if (!normalizedId) {
    return;
  }

  const hiddenIds = getHiddenDefaultProjectIds();
  if (hiddenIds.includes(normalizedId)) {
    return;
  }

  saveHiddenDefaultProjectIds([...hiddenIds, normalizedId]);
};

export const showDefaultProject = (projectId: number | string) => {
  const normalizedId = normalizeProjectId(projectId);
  const nextHiddenIds = getHiddenDefaultProjectIds().filter((id) => id !== normalizedId);

  saveHiddenDefaultProjectIds(nextHiddenIds);
};

export const clearHiddenDefaultProjects = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(HIDDEN_DEFAULT_PROJECT_IDS_KEY);
  emitProjectsUpdated();
};

export const clearAdminProjects = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(ADMIN_PROJECTS_KEY);
  emitProjectsUpdated();
};
